import { Text } from '../../typography/text/Text';
import defaultStyles from './QuoteCallout.module.scss';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import * as React from 'react';
import Button from '../../atoms/button';
import mergeClasses from '../../../utils/shallowMerge';
import { StylesCustomizer } from '../stylesCustomizer';

const QuoteCallout = React.forwardRef(
  (
    {
      author,
      authorTitle,
      quoteAuthorStyles,
      name,
      text,
      quoteTextStyles,
      image,
      alignQuote,
      alignElement,
      borders,
      buttonUrl,
      buttonTitle,
      buttonType = 'secondary',
      sizeQuote,
      sizeAuthor,
      textTransformQuote,
      textTransformAuthor,
      weightQuote,
      weightAuthor,
      className,
      customClasses,
      ...rest
    },
    ref,
  ) => {
    // determine whether 'text' is image URL from CT or quote text
    const styles = mergeClasses(defaultStyles, customClasses);

    const isURLregexp = /^http[s]?:\/\/(www\.)?(.*)?\/?(.)*/;
    const isImageLink = isURLregexp.test(text);

    const getButtonAlign = () => {
      if (!quoteAuthorStyles) return null;
      const styles = JSON.parse(quoteAuthorStyles);
      return {
        '--button-align': styles.Global?.['text-align'] || 'left',
      };
    }

    const QuoteText = (variables) => {
      return (
        <Text
          as="p"
          className={styles.textQuote}
          align={alignQuote}
          size={sizeQuote}
          textTransform={textTransformQuote}
          weight={weightQuote}
          variables={variables}
        >
          <span
            dangerouslySetInnerHTML={{
              __html: text,
            }}
          />
        </Text>
      );
    };

    const QuoteAuthor = (variables) => {
      return (
        <Text
          as="p"
          className={styles.textAuthor}
          align={alignQuote}
          size={sizeAuthor}
          textTransform={textTransformAuthor}
          weight={weightAuthor}
          variables={variables}
        >
          <span
            dangerouslySetInnerHTML={{
              __html: author,
            }}
          />
          {authorTitle && ','}
          <br />
          {authorTitle && (
            <>
              &#8211;{' '}
              <span
                dangerouslySetInnerHTML={{
                  __html: authorTitle,
                }}
              />{' '}
              &#8211;
            </>
          )}
        </Text>
      );
    };

    const StyledQuoteText = StylesCustomizer(QuoteText);
    const StyledQuoteAuthor = StylesCustomizer(QuoteAuthor);

    return (
      <div
        className={clsx(
          styles.root,
          alignElement && styles[`${alignElement}Align`],
          className,
        )}
        {...rest}
        ref={ref}
      >
        {(text || author) && (
          <div className={clsx(styles.textWrapper, borders && styles.borders)}>
            {text && text !== '' && !isImageLink && (
              <StyledQuoteText styles={quoteTextStyles} />
            )}
            {isImageLink && (
              <div
                className={clsx(styles.imageWrapper, {
                  [`${styles.imagePDP}`]: isImageLink,
                })}
              >
                <img src={text} alt="quote image" />
              </div>
            )}
            {author && author !== '' && (
              <StyledQuoteAuthor styles={quoteAuthorStyles} />
            )}
            {(buttonTitle && buttonUrl) && (
              <div className={styles.btnWrap} style={getButtonAlign()}>
                <Button
                  variant={buttonType}
                  nodeLink={buttonUrl}
                  className={clsx(styles.button, styles[`type_${buttonType}`], styles[`align_${alignQuote}`])}
                >
                  {buttonTitle}
                </Button>
              </div>
            )}
          </div>
        )}

        {image && (
          <div className={clsx(styles.imageWrapper)}>
            <div className={clsx(styles.image)}>
              <img src={image.file} alt={image.name} />
            </div>
          </div>
        )}
      </div>
    );
  },
);

QuoteCallout.propTypes = {
  text: PropTypes.string,
  author: PropTypes.string,
  imageimage: PropTypes.string,
  alignQuote: PropTypes.oneOf(['start', 'center', 'end']),
  alignElement: PropTypes.oneOf(['start', 'center', 'end']),
  sizeQuote: PropTypes.oneOf([
    'xxxs',
    'xxs',
    'xs',
    'sm',
    'md',
    'lg',
    'xl',
    'xxl',
    'xxxl',
  ]),
  sizeAuthor: PropTypes.oneOf([
    'xxxs',
    'xxs',
    'xs',
    'sm',
    'md',
    'lg',
    'xl',
    'xxl',
    'xxxl',
  ]),
  textTransformQuote: PropTypes.oneOf([
    'uppercase',
    'lowercase',
    'capitalize',
    'firstletter',
    'none',
  ]),
  textTransformAuthor: PropTypes.oneOf([
    'uppercase',
    'lowercase',
    'capitalize',
    'firstletter',
    'none',
  ]),
  weightQuote: PropTypes.oneOf(['normal', 'light', 'semibold', 'bold']),
  weightAuthor: PropTypes.oneOf(['normal', 'light', 'semibold', 'bold']),
  borders: PropTypes.bool,
  className: PropTypes.string,
};

QuoteCallout.defaultProps = {
  text: null,
  author: null,
  imageimage: null,
  borders: false,
  alignElement: 'center',
  sizeQuote: 'sm',
  sizeAuthor: 'xl',
  textTransformQuote: 'none',
  textTransformAuthor: 'none',
  weightQuote: 'light',
  weightAuthor: 'semibold',
  className: '',
};

export default QuoteCallout;
